import * as yup from "yup";
import i18next from "../../i18n";

/**
 * The offer-wide "general conditions" form. Repeats the field set the
 * pre-Carbon `Offer.tsx#renderGeneralConditions` collected and
 * `validateGeneralConditions()` gated on — now a real RHF/Yup form so the
 * Payment Method field can be a labelled `SelectField` (Task 5, ruling 3) and
 * the required-field gate is a schema rather than a hand-rolled boolean map.
 */
export interface OfferConditionsForm {
  offerValidity: string;
  paymentMethod: string;
  paymentTerm: string;
  leadTime: string;
  deliveryToBuyer: boolean;
  deliveryCostIncluded: boolean;
  additionalDeliveryCost: string;
  deliveryCostCurrency: string;
  pickupAddress: string;
  generalComments: string;
}

/**
 * SINGLE SOURCE OF TRUTH for offer-general-conditions validation, mirroring the
 * pre-Carbon `validateGeneralConditions()` EXACTLY:
 *   - offerValidity, paymentMethod (trimmed), paymentTerm and leadTime are
 *     always required;
 *   - additionalDeliveryCost and its deliveryCostCurrency are required ONLY
 *     when the buyer-delivery branch is active and its cost is not marked
 *     included — i.e.
 *     `!hasServiceMaterial && deliveryToBuyer && !deliveryCostIncluded` — and
 *     otherwise optional (SCRUM-36 added the currency).
 *
 * `hasServiceMaterial` is a submission-wide fact (any line is a SERVICE), not a
 * form field, so it's a factory argument rather than a `.when()` dependency.
 */
export const makeOfferConditionsSchema = (
  hasServiceMaterial: boolean,
  buyerPicksUp = false
) => {
  // On an order the buyer picks up (SCRUM-70) the supplier delivers nothing: no logistics to price.
  const chargesDeliveryCost = (
    deliveryToBuyer: boolean,
    deliveryCostIncluded: boolean
  ) =>
    !hasServiceMaterial &&
    !buyerPicksUp &&
    deliveryToBuyer &&
    !deliveryCostIncluded;

  return yup.object({
    offerValidity: yup
      .string()
      .trim()
      .required(i18next.t("validations.offerValidityRequired")),
    paymentMethod: yup
      .string()
      .trim()
      .required(i18next.t("validations.selectPaymentMethod")),
    paymentTerm: yup
      .string()
      .trim()
      .required(i18next.t("validations.paymentTermRequired")),
    leadTime: yup
      .string()
      .trim()
      .required(i18next.t("validations.leadTimeRequired")),
    deliveryToBuyer: yup.boolean().default(true),
    deliveryCostIncluded: yup.boolean().default(true),
    additionalDeliveryCost: yup
      .string()
      .when(["deliveryToBuyer", "deliveryCostIncluded"], {
        is: chargesDeliveryCost,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        then: (schema: any) =>
          schema.required(i18next.t("validations.deliveryCostRequired")),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        otherwise: (schema: any) => schema.optional(),
      }),
    deliveryCostCurrency: yup
      .string()
      .when(["deliveryToBuyer", "deliveryCostIncluded"], {
        is: chargesDeliveryCost,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        then: (schema: any) =>
          schema.trim().required(i18next.t("validations.currencyRequired")),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        otherwise: (schema: any) => schema.optional(),
      }),
    pickupAddress: buyerPicksUp
      ? yup
          .string()
          .trim()
          .required(i18next.t("validations.pickupAddressRequired"))
      : yup.string().default(""),
    generalComments: yup.string().default(""),
  });
};

export type OfferConditionsSchema = ReturnType<
  typeof makeOfferConditionsSchema
>;
